import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, ChevronLeft, ChevronRight, Leaf, Recycle, Users, Check } from 'lucide-react';

const CutleryTableware = () => {
  // Carousel state and refs
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Carousel images - 6 cutlery product images
  const carouselImages = [
    { src: require('../assets/Cutlery/tastymaxi_7.jpg'), alt: 'Edible Spoons', title: 'Edible Spoons' },
    { src: require('../assets/Cutlery/tastymaxi_77.jpg'), alt: 'Edible Spoons', title: 'Edible Spoons' },
    { src: require('../assets/Cutlery/tastymaxi_777.jpg'), alt: 'Edible Spoons', title: 'Edible Spoons' },
    { src: require('../assets/Cutlery/tastymaxi_12.jpg'), alt: 'Edible Spoons', title: 'Edible Spoons' },
  ];

  const totalSlides = carouselImages.length;

  // Go to next slide
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  // Go to previous slide
  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  // Go to specific slide
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Auto-play functionality
  useEffect(() => {
    if (!isPaused) {
      autoPlayRef.current = setInterval(() => {
        nextSlide();
      }, 3500); // Change slide every 3.5 seconds
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isPaused, nextSlide]);

  // Touch handlers for mobile swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };
  const products = [
    {
      name: "Edible Spoons",
      description: "Our edible spoons are the environmentally friendly alternative to disposable plastic. Made from natural ingredients and certified according to ISO 22000, FSSAI, and FDA, they are ideal for ice cream, yogurt, or soups. Stable, delicious, and long-lasting, the perfect choice for events, catering, and conscious consumption.",
      image: require('../assets/images/spoon.jpg'),
      link: "/cutlery-tableware/edible-spoons",
      available: true
    },
    {
      name: "Edible Forks",
      description: "The edible fork combines stability, functionality, and enjoyment in a plastic-free, eco-friendly utensil. Whether for pasta, salads, or cakes, this crispy and durable fork enables a sustainable and delicious dining experience for takeaways, events, or everyday use.",
      image: require('../assets/images/fork.jpg'),
      link: "/cutlery-tableware/edible-forks",
      available: true
    },
    {
      name: "Edible Coffee Stirrer",
      description: "The edible coffee stir sticks are the innovative, sustainable alternative to conventional wood stir sticks. Ideal for coffee specialties, and in the XL version perfect for tall latte macchiato glasses.",
      image: require('../assets/images/stirrer.jpg'),
      link: "/cutlery-tableware/edible-coffee-stirrer",
      available: true
    },
    {
      name: "Edible Ice Cream Spoon",
      description: "These mini spoons combine innovation, enjoyment, and sustainability in a small, practical dessert spoon. Whether vanilla or chocolate, for soft serve, yogurt, sorbet, or harder ice cream, it fits perfectly in the hand and ensures an unforgettable taste experience. Made from natural ingredients, plastic-free, and with a long lifespan.",
      image: require('../assets/images/icecream.jpg'),
      link: "/cutlery-tableware/edible-ice-cream-spoon",
      available: true
    },
    {
      name: "French Fries Fork",
      description: "The fork combines unique ketchup flavor, practical use, and sustainability in an edible chip fork. Perfect for fries, currywurst, or finger food, plastic-free, crispy, and eco-friendly.",
      image: require('../assets/images/fries.jpg'),
      link: "/cutlery-tableware/french-fries-fork",
      available: true
    }
  ];

  return (
    <div className="cutlery-page">
      {/* Hero Section */}
      <section className="products-hero">
        <div className="container">
          <h1 className="display-medium">Cutlery & Tableware EATlery® FRENVI</h1>
          <p className="body-large">
            Sustainable, edible cutlery and tableware solutions for a greener future. 
            Perfect for events, food service, and eco-conscious businesses.
          </p>
        </div>
      </section>

      {/* Featured Value Proposition Section */}
      <section className="sustainable-serving-section">
        <div className="container">
          <div className="sustainable-serving-content">
            <div className="sustainable-serving-text">
              <span className="sustainable-badge">
                <Leaf size={16} />
                Eco-Friendly Dining
              </span>
              <h2 className="sustainable-headline">Sustainable Serving, Made Simple</h2>
              <p className="sustainable-description">
                Discover the practical alternative to plastic cutlery. Our edible utensils are 
                sustainable, cost-conscious, and easy to source in any quantity, perfect for 
                businesses ready to make a meaningful environmental impact.
              </p>
              
              <ul className="sustainable-benefits-list">
                <li>
                  <span className="benefit-icon"><Check size={18} /></span>
                  <span>Usable and edible options available</span>
                </li>
                <li>
                  <span className="benefit-icon"><Recycle size={18} /></span>
                  <span>Reduces single-use plastic waste</span>
                </li>
                <li>
                  <span className="benefit-icon"><Users size={18} /></span>
                  <span>Suitable for events, hospitality, and daily use</span>
                </li>
              </ul>

              <div className="sustainable-cta">
                <Link to="/contact" className="btn btn-primary">
                  Get a Quote
                </Link>
              </div>
            </div>

            <div className="sustainable-serving-image">
              <img 
                src="/assets/images/untitled_design.png" 
                alt="Edible cutlery collection with spoons, forks, and stirrers" 
              />
              <div className="image-overlay-accent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="packaging-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Our Edible Cutlery Collection</h2>
            <p className="body-large">
              Explore our range of sustainable, edible cutlery and tableware products.
            </p>
          </div>

          <div className="products-grid cutlery-products-grid">
            {products.map((product, index) => (
              <Link 
                key={index} 
                to={product.link}
                className="product-card cutlery-product-card clickable-card"
              >
                <div className="cutlery-product-image">
                  <img src={product.image} alt={product.name} />
                  <div className="product-overlay">
                    <span className="view-product">
                      View Product <ArrowRight size={16} />
                    </span>
                  </div>
                </div>
                <h3 className="heading-3">{product.name}</h3>
                <p className="body-medium">{product.description}</p>
                {product.available && (
                  <span className="availability-badge">Available Now</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Product Carousel Section */}
      <section className="cutlery-carousel-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Explore Our Cutlery Range</h2>
            <p className="body-large">
              Discover our innovative sustainable cutlery products
            </p>
          </div>

          <div 
            className="cutlery-carousel"
            ref={carouselRef}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Previous Button */}
            <button 
              className="carousel-btn carousel-btn-prev" 
              onClick={prevSlide}
              aria-label="Previous slide"
            >
              <ChevronLeft size={28} />
            </button>

            {/* Slides Container */}
            <div className="carousel-slides-container">
              <div 
                className="carousel-slides"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {carouselImages.map((image, index) => (
                  <div key={index} className="carousel-slide">
                    <img src={image.src} alt={image.alt} />
                    <div className="carousel-slide-overlay">
                      <span className="carousel-slide-title">{image.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Button */}
            <button 
              className="carousel-btn carousel-btn-next" 
              onClick={nextSlide}
              aria-label="Next slide"
            >
              <ChevronRight size={28} />
            </button>

            {/* Dots Indicator */}
            <div className="carousel-dots">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="carousel-cta">
            <Link to="/contact" className="btn btn-primary">
              Contact Us About Our Products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CutleryTableware;
